import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Plus,
  Edit,
  Trash2,
  User,
  Users,
  Calendar,
  DollarSign,
  Phone,
  Building,
  Eye,
  X,
  Download,
  Clock,
  FileText,
  DoorOpen,
  Search,
  SlidersHorizontal
} from 'lucide-react';
import { roomsAPI, billingAPI, paymentsAPI } from '../services/api';
import { tenantsAPI } from '../services/tenantsAPI';
import { formatCurrency, formatDate, formatDateForInput } from '../utils/helpers';
import toast from 'react-hot-toast';
import DeleteConfirmModal from '../components/DeleteConfirmModal';

const RoomsPage = () => {
  const navigate = useNavigate();
  const [rooms, setRooms] = useState([]);
  const [roomSummaries, setRoomSummaries] = useState({});
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [roomToDelete, setRoomToDelete] = useState(null);
  const [showOccupantsModal, setShowOccupantsModal] = useState(false);
  const [occupantsRoom, setOccupantsRoom] = useState(null);
  const [occupants, setOccupants] = useState([]);
  const [occupantsLoading, setOccupantsLoading] = useState(false);
  const [editingRoom, setEditingRoom] = useState(null);
  const [search, setSearch] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [formData, setFormData] = useState({
    room_number: '',
    rent_amount: '',
    tenant_name: '',
    tenant_contact: '',
    move_in_date: '',
    rent_due_date: 1,
    initial_meter_reading: 0
  });

  useEffect(() => {
    fetchRoomsData();
  }, []);

  const fetchRoomsData = async () => {
    try {
      const response = await roomsAPI.getAll();
      const roomsData = response.data.rooms;
      setRooms(roomsData);

      // Fetch financial summaries for occupied rooms
      const summaries = {};
      for (const room of roomsData) {
        if (room.is_occupied) {
          try {
            const summaryResponse = await billingAPI.getRoomSummary(room.id);
            summaries[room.id] = summaryResponse.data;
          } catch (error) {
            console.error(`Error fetching summary for room ${room.id}:`, error);
            summaries[room.id] = {
              total_outstanding: 0,
              current_bill: null,
              overdue_months: [],
              available_credits: 0
            };
          }
        }
      }
      setRoomSummaries(summaries);
    } catch (error) {
      console.error('Error fetching rooms:', error);
      toast.error('Failed to load rooms data');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      if (editingRoom) {
        await roomsAPI.update(editingRoom.id, formData);
        toast.success('Room updated successfully');
      } else {
        await roomsAPI.create(formData);
        toast.success('Room created successfully');
      }
      
      fetchRoomsData();
      closeModal();
    } catch (error) {
      console.error('Error saving room:', error);
      toast.error(error.response?.data?.error || 'Failed to save room');
    }
  };

  const handleDelete = async (room) => {
    setRoomToDelete(room);
    setShowDeleteModal(true);
  };

  const handleMarkVacant = async (room) => {
    if (!window.confirm(`Mark Room ${room.room_number} as vacant? This will clear the tenant details and stop arrears from accruing.`)) return;
    try {
      await roomsAPI.markVacant(room.id);
      toast.success(`Room ${room.room_number} marked as vacant`);
      fetchRoomsData();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to mark room as vacant');
    }
  };

  const confirmDelete = async () => {
    if (!roomToDelete) return;
    
    try {
      await roomsAPI.delete(roomToDelete.id);
      toast.success('Room deleted successfully');
      fetchRoomsData();
    } catch (error) {
      console.error('Error deleting room:', error);
      toast.error('Failed to delete room');
    }
  };

  const openModal = (room = null) => {
    if (room) {
      setEditingRoom(room);
      setFormData({
        room_number: room.room_number,
        rent_amount: room.rent_amount,
        tenant_name: room.tenant_name || '',
        tenant_contact: room.tenant_contact || '',
        move_in_date: room.move_in_date ? formatDateForInput(new Date(room.move_in_date)) : '',
        rent_due_date: room.rent_due_date || 1,
        initial_meter_reading: room.initial_meter_reading || 0
      });
    } else {
      setEditingRoom(null);
      setFormData({
        room_number: '',
        rent_amount: '',
        tenant_name: '',
        tenant_contact: '',
        move_in_date: '',
        rent_due_date: 1,
        initial_meter_reading: 0
      });
    }
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingRoom(null);
  };

  const openOccupantsModal = async (room) => {
    setOccupantsRoom(room);
    setOccupants([]);
    setShowOccupantsModal(true);
    setOccupantsLoading(true);
    try {
      const res = await tenantsAPI.getAll();
      const all = res.data.tenants || [];
      setOccupants(all.filter(t => t.room_id === room.id));
    } catch {
      toast.error('Failed to load occupants');
    } finally {
      setOccupantsLoading(false);
    }
  };

  const handleOpenDoc = async (filename) => {
    try {
      const response = await tenantsAPI.downloadFile(filename);
      const url = URL.createObjectURL(response.data);
      const tab = window.open(url, '_blank', 'noreferrer');
      if (!tab) toast.error('Please allow pop-ups to view documents');
      setTimeout(() => URL.revokeObjectURL(url), 15000);
    } catch (err) {
      toast.error('Failed to open document');
    }
  };

  const handleDownloadRoomPDF = async (room) => {
    try {
      // Fetch room summary and payment data
      const summaryResponse = await billingAPI.getRoomSummary(room.id);
      const paymentsResponse = await paymentsAPI.getAll({ room_id: room.id });
      
      const summary = summaryResponse.data;
      const payments = paymentsResponse.data.payments || [];
      
      // Calculate totals
      const totalRentPaid = payments
        .filter(p => p.payment_type === 'rent' || p.payment_type === 'both')
        .reduce((sum, p) => sum + (p.amount || 0), 0);
      
      const totalElectricityPaid = payments
        .filter(p => p.payment_type === 'electricity' || p.payment_type === 'both')
        .reduce((sum, p) => sum + (p.amount || 0), 0);

      const totalOutstanding = summary.total_outstanding || 0;
      const overdueMonths = summary.overdue_months || [];

      // Format date as "Wed 20-Jan-2025"
      const formatPDFDate = (dateString) => {
        if (!dateString) return 'N/A';
        const date = new Date(dateString);
        const days = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
        const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
        return `${days[date.getDay()]} ${date.getDate()}-${months[date.getMonth()]}-${date.getFullYear()}`;
      };

      // Short payment type labels for compact display
      const getShortPaymentType = (type) => {
        const typeMap = {
          'rent': 'Rent',
          'electricity': 'Elec',
          'both': 'Both',
          'other': 'Other'
        };
        return typeMap[type] || type || 'N/A';
      };

      const htmlContent = `
        <!DOCTYPE html>
        <html>
        <head>
          <meta charset="UTF-8">
          <title>Room ${room.room_number} Report - ${new Date().toLocaleDateString('en-IN', { year: 'numeric', month: 'short', day: 'numeric' })}</title>
          <style>
            @page {
              size: A4;
              margin: 15mm;
            }
            body { 
              font-family: Arial, sans-serif; 
              margin: 0;
              padding: 40px;
              background: white;
            }
            .header { 
              text-align: center; 
              margin-bottom: 30px; 
              border-bottom: 3px solid #3b82f6; 
              padding-bottom: 20px; 
            }
            .header h1 { 
              color: #1e40af; 
              margin: 0; 
              font-size: 28px; 
            }
            .header p { 
              color: #6b7280; 
              margin: 5px 0; 
            }
            .print-button {
              position: fixed;
              top: 20px;
              right: 20px;
              background: #3b82f6;
              color: white;
              border: none;
              padding: 12px 24px;
              border-radius: 8px;
              font-size: 16px;
              font-weight: 600;
              cursor: pointer;
              box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
              z-index: 1000;
              transition: background 0.3s;
            }
            .print-button:hover {
              background: #2563eb;
            }
            .room-info { 
              background: #f3f4f6; 
              padding: 15px; 
              border-radius: 8px; 
              margin-bottom: 20px; 
            }
            .room-info h3 { 
              margin: 0 0 10px 0; 
              color: #374151; 
              font-size: 16px; 
            }
            .room-info p { 
              margin: 5px 0; 
              color: #6b7280; 
            }
            .summary { 
              display: grid; 
              grid-template-columns: repeat(3, 1fr); 
              gap: 15px; 
              margin-bottom: 30px; 
            }
            .summary-card { 
              background: #eff6ff; 
              padding: 15px; 
              border-radius: 8px; 
              text-align: center; 
              border-left: 4px solid #3b82f6; 
            }
            .summary-card.green { 
              background: #f0fdf4; 
              border-left-color: #10b981; 
            }
            .summary-card.red { 
              background: #fef2f2; 
              border-left-color: #ef4444; 
            }
            .summary-card h4 { 
              margin: 0; 
              color: #6b7280; 
              font-size: 14px; 
              font-weight: normal; 
            }
            .summary-card p { 
              margin: 10px 0 0 0; 
              color: #1e40af; 
              font-size: 24px; 
              font-weight: bold; 
            }
            .summary-card.green p { 
              color: #059669; 
            }
            .summary-card.red p { 
              color: #dc2626; 
            }
            table { 
              width: 100%; 
              border-collapse: collapse; 
              margin-top: 20px; 
              font-size: 11px;
            }
            th { 
              background: #1e40af; 
              color: white; 
              padding: 8px 6px; 
              text-align: left; 
              font-weight: 600; 
              font-size: 11px;
            }
            td { 
              padding: 6px 6px; 
              border-bottom: 1px solid #e5e7eb; 
              font-size: 11px;
            }
            tr:nth-child(even) { 
              background: #f9fafb; 
            }
            .overdue-section {
              margin-top: 30px;
              padding: 0;
              background: transparent;
              border: none;
            }
            .overdue-section h3 {
              color: #dc2626;
              margin: 0 0 15px 0;
              font-size: 18px;
            }
            .overdue-table {
              width: 100%;
              border-collapse: collapse;
              margin-top: 10px;
            }
            .overdue-table th {
              background: #dc2626;
              color: white;
              padding: 8px 6px;
              text-align: left;
              font-weight: 600;
              font-size: 11px;
            }
            .overdue-table td {
              padding: 6px 6px;
              border-bottom: 1px solid #e5e7eb;
              font-size: 11px;
            }
            .overdue-table .status-critical {
              background: #fef2f2;
              color: #dc2626;
              font-weight: bold;
            }
            .overdue-table .status-warning {
              background: #fffbeb;
              color: #d97706;
              font-weight: bold;
            }
            .footer { 
              margin-top: 30px; 
              text-align: center; 
              color: #9ca3af; 
              font-size: 12px; 
              border-top: 1px solid #e5e7eb; 
              padding-top: 20px; 
            }
            @media print {
              .print-button {
                display: none;
              }
              body {
                padding: 0;
              }
              .summary-card,
              .room-info,
              .overdue-section,
              tr:nth-child(even) {
                -webkit-print-color-adjust: exact;
                print-color-adjust: exact;
              }
            }
          </style>
        </head>
        <body>
          <button class="print-button" onclick="window.print()">🖨️ Print / Save as PDF</button>
          
          <div class="header">
            <h1>Room ${room.room_number} - Detailed Report</h1>
            <p>Generated on ${new Date().toLocaleDateString('en-IN', { year: 'numeric', month: 'long', day: 'numeric', hour: '2-digit', minute: '2-digit' })}</p>
          </div>
          
          <div class="room-info">
            <h3>Room Information</h3>
            <p><strong>Room Number:</strong> ${room.room_number}</p>
            <p><strong>Monthly Rent:</strong> ${formatCurrency(room.rent_amount)}</p>
            ${room.tenant_name ? `<p><strong>Tenant:</strong> ${room.tenant_name}</p>` : ''}
            ${room.tenant_contact ? `<p><strong>Contact:</strong> ${room.tenant_contact}</p>` : ''}
            ${room.move_in_date ? `<p><strong>Move-in Date:</strong> ${formatDate(room.move_in_date)}</p>` : ''}
            <p><strong>Status:</strong> ${room.is_occupied ? 'Occupied' : 'Vacant'}</p>
          </div>
          
          <div class="summary">
            <div class="summary-card green">
              <h4>Total Rent Paid</h4>
              <p>${formatCurrency(totalRentPaid)}</p>
            </div>
            <div class="summary-card green">
              <h4>Total Electricity Paid</h4>
              <p>${formatCurrency(totalElectricityPaid)}</p>
            </div>
            <div class="summary-card ${totalOutstanding > 0 ? 'red' : 'green'}">
              <h4>Total Outstanding</h4>
              <p>${formatCurrency(totalOutstanding)}</p>
            </div>
          </div>
          
          ${overdueMonths.length > 0 ? `
          <div class="overdue-section">
            <h3>⚠️ Outstanding Arrears</h3>
            <table class="overdue-table">
              <thead>
                <tr>
                  <th>Room</th>
                  <th>Tenant</th>
                  <th>Type</th>
                  <th>Due Date</th>
                  <th>Amount</th>
                  <th>Days Overdue</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                ${overdueMonths.map(month => {
                  const daysOverdue = month.days_overdue || 0;
                  const status = daysOverdue > 180 ? 'Critical' : daysOverdue > 60 ? 'Warning' : 'Overdue';
                  const statusClass = daysOverdue > 180 ? 'status-critical' : daysOverdue > 60 ? 'status-warning' : '';
                  const monthName = month.month ? getMonthName(month.month) : '';
                  const dueDate = `${monthName} ${month.year || ''}`;
                  const amount = month.amount || month.outstanding || month.total_amount || 0;
                  
                  return `
                    <tr>
                      <td>Room ${room.room_number}</td>
                      <td>${room.tenant_name || 'Vacant'}</td>
                      <td>Rent</td>
                      <td>${dueDate}</td>
                      <td>${formatCurrency(amount)}</td>
                      <td>${daysOverdue} days</td>
                      <td class="${statusClass}">${status}</td>
                    </tr>
                  `;
                }).join('')}
              </tbody>
            </table>
          </div>
          ` : ''}
          
          ${payments.length > 0 ? `
          <h3 style="margin-top: 30px; color: #374151;">Payment History</h3>
          <table>
            <thead>
              <tr>
                <th>Date</th>
                <th>Type</th>
                <th>Amount</th>
                <th>Method</th>
                <th>Notes</th>
              </tr>
            </thead>
            <tbody>
              ${payments.map(payment => `
                <tr>
                  <td>${formatPDFDate(payment.transaction_date || payment.payment_date)}</td>
                  <td>${getShortPaymentType(payment.payment_type)}</td>
                  <td>${formatCurrency(payment.amount || 0)}</td>
                  <td style="text-transform: capitalize;">${payment.payment_method || 'N/A'}</td>
                  <td>${payment.notes || '-'}</td>
                </tr>
              `).join('')}
            </tbody>
          </table>
          ` : '<p style="text-align: center; color: #9ca3af; margin-top: 30px;">No payment history available</p>'}
          
          <div class="footer">
            <p>Tenant Bills Management System © ${new Date().getFullYear()}</p>
          </div>
        </body>
        </html>
      `;

      // Open HTML in new window/tab
      const newWindow = window.open('', '_blank');
      newWindow.document.open();
      newWindow.document.write(htmlContent);
      newWindow.document.close();
      
      // Ensure title is set
      setTimeout(() => {
        if (newWindow && !newWindow.closed) {
          newWindow.document.title = `Room ${room.room_number} Report - ${new Date().toLocaleDateString('en-IN', { year: 'numeric', month: 'short', day: 'numeric' })}`;
        }
      }, 100);
      
      toast.success('Report opened in new tab! Click the print button to save as PDF.');
    } catch (error) {
      console.error('Error generating room report:', error);
      toast.error('Failed to generate room report');
    }
  };

  const getStatusColor = (isOccupied) => {
    return isOccupied
      ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
      : 'bg-gray-100 text-gray-800 dark:bg-secondary-800 dark:text-secondary-200';
  };

  const getMonthName = (monthNumber) => {
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    return months[monthNumber - 1] || '';
  };

  const pageStats = useMemo(() => {
    const total = rooms.length;
    const occupied = rooms.filter(r => r.is_occupied).length;
    const vacant = total - occupied;
    const monthlyIncome = rooms.filter(r => r.is_occupied).reduce((s, r) => s + parseFloat(r.rent_amount || 0), 0);
    const totalOutstanding = Object.values(roomSummaries).reduce((s, sum) => s + (sum?.total_outstanding || 0), 0);
    const occupancyPct = total > 0 ? Math.round((occupied / total) * 100) : 0;
    return { total, occupied, vacant, monthlyIncome, totalOutstanding, occupancyPct };
  }, [rooms, roomSummaries]);

  const filteredRooms = useMemo(() => {
    return rooms.filter(r => {
      if (search && !r.room_number?.toLowerCase().includes(search.toLowerCase()) &&
          !r.tenant_name?.toLowerCase().includes(search.toLowerCase())) return false;
      if (filterStatus === 'occupied' && !r.is_occupied) return false;
      if (filterStatus === 'vacant' && r.is_occupied) return false;
      if (filterStatus === 'arrears' && !(roomSummaries[r.id]?.total_outstanding > 0)) return false;
      return true;
    });
  }, [rooms, roomSummaries, search, filterStatus]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="loading-spinner"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-secondary-900 dark:text-secondary-100">Room Management</h1>
          <p className="text-secondary-600 dark:text-secondary-300">Manage rooms and tenant information</p>
        </div>
        <motion.button
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
          onClick={() => openModal()}
          className="btn-primary flex items-center space-x-2"
        >
          <Plus className="w-5 h-5" />
          <span>Add Room</span>
        </motion.button>
      </div>

      {/* Portfolio Stats Bar */}
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
        <div className="card p-4">
          <p className="text-xs text-secondary-500 dark:text-secondary-400 uppercase tracking-wide mb-1">Total Rooms</p>
          <p className="text-2xl font-bold text-secondary-900 dark:text-secondary-100">{pageStats.total}</p>
        </div>
        <div className="card p-4">
          <p className="text-xs text-secondary-500 dark:text-secondary-400 uppercase tracking-wide mb-1">Occupied</p>
          <p className="text-2xl font-bold text-primary-600 dark:text-primary-400">{pageStats.occupied}</p>
          <p className="text-xs text-secondary-400 dark:text-secondary-500 mt-0.5">{pageStats.occupancyPct}% occupancy</p>
        </div>
        <div className="card p-4">
          <p className="text-xs text-secondary-500 dark:text-secondary-400 uppercase tracking-wide mb-1">Vacant</p>
          <p className="text-2xl font-bold text-secondary-500 dark:text-secondary-300">{pageStats.vacant}</p>
          <p className="text-xs text-secondary-400 dark:text-secondary-500 mt-0.5">{pageStats.total - pageStats.occupied} available</p>
        </div>
        <div className="card p-4">
          <p className="text-xs text-secondary-500 dark:text-secondary-400 uppercase tracking-wide mb-1">Monthly Income</p>
          <p className="text-xl font-bold text-green-600 dark:text-green-400">{formatCurrency(pageStats.monthlyIncome)}</p>
          <p className="text-xs text-secondary-400 dark:text-secondary-500 mt-0.5">from occupied rooms</p>
        </div>
        <div className="card p-4 col-span-2 lg:col-span-1">
          <p className="text-xs text-secondary-500 dark:text-secondary-400 uppercase tracking-wide mb-1">Total Outstanding</p>
          <p className={`text-xl font-bold ${pageStats.totalOutstanding > 0 ? 'text-red-600 dark:text-red-400' : 'text-green-600 dark:text-green-400'}`}>
            {formatCurrency(pageStats.totalOutstanding)}
          </p>
          <p className="text-xs text-secondary-400 dark:text-secondary-500 mt-0.5">all arrears</p>
        </div>
      </div>

      {/* Search & Filter Bar */}
      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-secondary-400" />
          <input
            type="text"
            placeholder="Search by room number or tenant name..."
            value={search}
            onChange={e => setSearch(e.target.value)}
            className="input-field pl-9 w-full"
          />
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <SlidersHorizontal className="w-4 h-4 text-secondary-400 flex-shrink-0" />
          {[['all', 'All'], ['occupied', 'Occupied'], ['vacant', 'Vacant'], ['arrears', 'With Arrears']].map(([val, label]) => (
            <button
              key={val}
              onClick={() => setFilterStatus(val)}
              className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                filterStatus === val
                  ? 'bg-primary-600 text-white'
                  : 'bg-secondary-100 dark:bg-secondary-700 text-secondary-600 dark:text-secondary-300 hover:bg-secondary-200 dark:hover:bg-secondary-600'
              }`}
            >
              {label}
            </button>
          ))}
        </div>
      </div>

      {/* Empty state */}
      {filteredRooms.length === 0 && !loading && (
        <div className="text-center py-12 text-secondary-400">
          <Building className="w-12 h-12 mx-auto mb-3 opacity-30" />
          <p className="text-lg font-medium">No rooms match your filters</p>
          <p className="text-sm mt-1">Try adjusting your search or filter criteria</p>
        </div>
      )}

      {/* Rooms Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        <AnimatePresence>
          {filteredRooms.map((room) => (
            <RoomCard
              key={room.id}
              room={room}
              summary={roomSummaries[room.id]}
              onEdit={() => openModal(room)}
              onDelete={() => handleDelete(room)}
              onMarkVacant={() => handleMarkVacant(room)}
              onDownloadPDF={() => handleDownloadRoomPDF(room)}
              onViewOccupants={() => openOccupantsModal(room)}
              getStatusColor={getStatusColor}
              getMonthName={getMonthName}
              navigate={navigate}
            />
          ))}
        </AnimatePresence>
      </div>

      {/* Add/Edit Room Modal */}
      <AnimatePresence>
        {showModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              className="bg-white dark:bg-secondary-800 rounded-lg p-6 w-full max-w-md max-h-[90vh] overflow-y-auto border border-secondary-200 dark:border-secondary-700"
            >
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-xl font-semibold text-secondary-900 dark:text-secondary-100">
                  {editingRoom ? 'Edit Room' : 'Add New Room'}
                </h2>
                <button
                  onClick={closeModal}
                  className="p-2 hover:bg-secondary-100 dark:hover:bg-secondary-700 rounded-lg transition-colors"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-secondary-700 dark:text-secondary-300 mb-1">
                    Room Number
                  </label>
                  <input
                    type="text"
                    value={formData.room_number}
                    onChange={(e) => setFormData({...formData, room_number: e.target.value})}
                    className="input-field"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-secondary-700 dark:text-secondary-300 mb-1">
                    Monthly Rent Amount
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    value={formData.rent_amount}
                    onChange={(e) => setFormData({...formData, rent_amount: e.target.value})}
                    className="input-field"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-secondary-700 dark:text-secondary-300 mb-1">
                    Tenant Name
                  </label>
                  <input
                    type="text"
                    value={formData.tenant_name}
                    onChange={(e) => setFormData({...formData, tenant_name: e.target.value})}
                    className="input-field"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-secondary-700 dark:text-secondary-300 mb-1">
                    Tenant Contact
                  </label>
                  <input
                    type="text"
                    value={formData.tenant_contact}
                    onChange={(e) => setFormData({...formData, tenant_contact: e.target.value})}
                    className="input-field"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-secondary-700 dark:text-secondary-300 mb-1">
                    Move-in Date
                  </label>
                  <input
                    type="date"
                    value={formData.move_in_date}
                    onChange={(e) => setFormData({...formData, move_in_date: e.target.value})}
                    className="input-field"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-secondary-700 dark:text-secondary-300 mb-1">
                    Rent Due Date (Day of Month)
                  </label>
                  <input
                    type="number"
                    min="1"
                    max="31"
                    value={formData.rent_due_date}
                    onChange={(e) => setFormData({...formData, rent_due_date: parseInt(e.target.value)})}
                    className="input-field"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-secondary-700 dark:text-secondary-300 mb-1">
                    Initial Meter Reading
                  </label>
                  <input
                    type="number"
                    value={formData.initial_meter_reading}
                    onChange={(e) => setFormData({...formData, initial_meter_reading: parseInt(e.target.value)})}
                    className="input-field"
                    required
                  />
                </div>

                <div className="flex space-x-3 pt-4">
                  <button
                    type="button"
                    onClick={closeModal}
                    className="btn-secondary flex-1"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="btn-primary flex-1"
                  >
                    {editingRoom ? 'Update' : 'Create'}
                  </button>
                </div>
              </form>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* Occupants Modal */}
      <AnimatePresence>
        {showOccupantsModal && occupantsRoom && (
          <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center p-4 z-50">
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 20 }}
              className="bg-white dark:bg-secondary-800 rounded-2xl shadow-2xl w-full max-w-lg max-h-[85vh] flex flex-col border border-secondary-200 dark:border-secondary-700 overflow-hidden"
            >
              {/* Modal header */}
              <div className="bg-gradient-to-br from-primary-500 to-primary-700 px-6 py-5 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-white bg-opacity-20 flex items-center justify-center">
                    <Users className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <h2 className="text-white font-bold text-lg leading-tight">Room {occupantsRoom.room_number} Occupants</h2>
                    <p className="text-primary-100 text-xs">{occupants.length} tenant{occupants.length !== 1 ? 's' : ''} found</p>
                  </div>
                </div>
                <button
                  onClick={() => setShowOccupantsModal(false)}
                  className="p-2 bg-white bg-opacity-20 hover:bg-opacity-30 text-white rounded-lg transition-colors"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>

              {/* Modal body */}
              <div className="overflow-y-auto flex-1 p-5 space-y-4">
                {occupantsLoading ? (
                  <div className="flex items-center justify-center py-12">
                    <div className="loading-spinner" />
                  </div>
                ) : occupants.length === 0 ? (
                  <div className="flex flex-col items-center justify-center py-12 text-secondary-400 dark:text-secondary-500">
                    <Users className="w-12 h-12 mb-3 opacity-40" />
                    <p className="text-sm font-medium">No tenants linked to this room</p>
                    <p className="text-xs mt-1">Add tenants from the Tenants page</p>
                  </div>
                ) : (
                  occupants.map(t => {
                    const initials = t.full_name.split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase();
                    const age = (() => {
                      if (!t.date_of_birth) return null;
                      const b = new Date(t.date_of_birth), today = new Date();
                      let y = today.getFullYear() - b.getFullYear();
                      const m = today.getMonth() - b.getMonth();
                      if (m < 0 || (m === 0 && today.getDate() < b.getDate())) y--;
                      return y;
                    })();
                    const tenancy = (() => {
                      if (!t.lease_start) return null;
                      const s = new Date(t.lease_start), today = new Date();
                      let y = today.getFullYear() - s.getFullYear();
                      let mo = today.getMonth() - s.getMonth();
                      if (today.getDate() < s.getDate()) mo--;
                      if (mo < 0) { y--; mo += 12; }
                      if (y === 0) return `${mo} mo`;
                      if (mo === 0) return `${y} yr${y !== 1 ? 's' : ''}`;
                      return `${y} yr${y !== 1 ? 's' : ''} ${mo} mo`;
                    })();
                    return (
                      <div key={t.id} className="bg-secondary-50 dark:bg-secondary-700/50 rounded-2xl overflow-hidden border border-secondary-200 dark:border-secondary-700">
                        {/* Tenant card header */}
                        <div className="flex items-center gap-3 px-4 py-3 border-b border-secondary-200 dark:border-secondary-600">
                          <div className="w-10 h-10 rounded-full bg-gradient-to-br from-primary-400 to-primary-600 flex items-center justify-center flex-shrink-0">
                            <span className="text-white text-sm font-bold">{initials}</span>
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="font-semibold text-secondary-900 dark:text-secondary-100 truncate">{t.full_name}</p>
                            <p className="text-xs text-secondary-500 dark:text-secondary-400">Active Tenant</p>
                          </div>
                        </div>
                        {/* Tenant stats */}
                        <div className="grid grid-cols-2 gap-0 divide-x divide-secondary-200 dark:divide-secondary-600">
                          <div className="px-4 py-3">
                            <p className="text-xs text-secondary-500 dark:text-secondary-400 flex items-center gap-1 mb-1"><User className="w-3 h-3" /> Age</p>
                            <p className="text-sm font-semibold text-secondary-900 dark:text-secondary-100">{age !== null ? `${age} yrs` : '—'}</p>
                            <p className="text-xs text-secondary-400 dark:text-secondary-500">{t.date_of_birth ? formatDate(t.date_of_birth) : 'N/A'}</p>
                          </div>
                          <div className="px-4 py-3">
                            <p className="text-xs text-secondary-500 dark:text-secondary-400 flex items-center gap-1 mb-1"><Clock className="w-3 h-3" /> Tenancy</p>
                            <p className="text-sm font-semibold text-secondary-900 dark:text-secondary-100">{tenancy || '—'}</p>
                            <p className="text-xs text-secondary-400 dark:text-secondary-500">{t.lease_start ? `Since ${formatDate(t.lease_start)}` : 'N/A'}</p>
                          </div>
                        </div>
                        {/* Documents */}
                        {(t.passport_path || t.id_card_path || t.other_doc_path) && (
                          <div className="px-4 py-2.5 border-t border-secondary-200 dark:border-secondary-600 flex flex-wrap gap-2">
                            {t.passport_path && (
                              <button onClick={() => handleOpenDoc(t.passport_path)}
                                className="inline-flex items-center gap-1 px-2.5 py-1 rounded-lg text-xs font-medium bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 hover:bg-blue-100 transition-colors">
                                <FileText className="w-3 h-3" /> Passport
                              </button>
                            )}
                            {t.id_card_path && (
                              <button onClick={() => handleOpenDoc(t.id_card_path)}
                                className="inline-flex items-center gap-1 px-2.5 py-1 rounded-lg text-xs font-medium bg-green-50 dark:bg-green-900/30 text-green-700 dark:text-green-300 hover:bg-green-100 transition-colors">
                                <FileText className="w-3 h-3" /> ID Card
                              </button>
                            )}
                            {t.other_doc_path && (
                              <button onClick={() => handleOpenDoc(t.other_doc_path)}
                                className="inline-flex items-center gap-1 px-2.5 py-1 rounded-lg text-xs font-medium bg-purple-50 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300 hover:bg-purple-100 transition-colors">
                                <FileText className="w-3 h-3" /> Other
                              </button>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })
                )}
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* Delete Confirmation Modal */}
      <DeleteConfirmModal
        isOpen={showDeleteModal}
        onClose={() => {
          setShowDeleteModal(false);
          setRoomToDelete(null);
        }}
        onConfirm={confirmDelete}
        itemName={roomToDelete ? `Room ${roomToDelete.room_number}` : ''}
        itemType="room"
      />
    </div>
  );
};

// Room Card Component with Unified Billing Display
const RoomCard = ({ room, summary, onEdit, onDelete, onMarkVacant, onDownloadPDF, onViewOccupants, getStatusColor, getMonthName, navigate }) => {
  const currentBill = summary?.current_bill;
  const totalOutstanding = summary?.total_outstanding || 0;
  const overdueMonths = summary?.overdue_months || [];
  const availableCredits = summary?.available_credits || 0;

  const handleBadgeClick = (month, year) => {
    navigate(`/room/${room.id}/bill/${year}/${month}`);
  };

  return (
    <motion.div
      layout
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -20 }}
      className="bg-white dark:bg-secondary-800 rounded-2xl shadow-sm border border-secondary-200 dark:border-secondary-700 overflow-hidden hover:shadow-md transition-shadow duration-200 flex flex-col"
    >
      {/* Gradient Header */}
      <div className={`relative px-5 pt-5 pb-4 ${room.is_occupied ? 'bg-gradient-to-br from-primary-500 to-primary-700' : 'bg-gradient-to-br from-secondary-400 to-secondary-600'}`}>
        {/* Action buttons */}
        <div className="absolute top-3 right-3 flex items-center gap-1">
          <button
            onClick={onDownloadPDF}
            className="p-1.5 bg-white bg-opacity-20 hover:bg-opacity-30 text-white rounded-lg transition-colors"
            title="Download PDF Report"
          >
            <Download className="w-3.5 h-3.5" />
          </button>
          <button
            onClick={onEdit}
            className="p-1.5 bg-white bg-opacity-20 hover:bg-opacity-30 text-white rounded-lg transition-colors"
            title="Edit"
          >
            <Edit className="w-3.5 h-3.5" />
          </button>
          <button
            onClick={onDelete}
            className="p-1.5 bg-white bg-opacity-20 hover:bg-red-500 hover:bg-opacity-80 text-white rounded-lg transition-colors"
            title="Delete"
          >
            <Trash2 className="w-3.5 h-3.5" />
          </button>
        </div>

        {/* Room number + status */}
        <div className="flex items-center gap-3 mt-1">
          <div className="w-12 h-12 rounded-xl bg-white bg-opacity-20 border border-white border-opacity-30 flex items-center justify-center flex-shrink-0">
            <Building className="w-6 h-6 text-white" />
          </div>
          <div>
            <h3 className="text-lg font-bold text-white leading-tight">Room {room.room_number}</h3>
            <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium mt-0.5 ${
              room.is_occupied
                ? 'bg-green-400 bg-opacity-30 text-white'
                : 'bg-white bg-opacity-20 text-white'
            }`}>
              {room.is_occupied
                ? `Occupied${typeof room.occupant_count !== 'undefined' ? ` · ${room.occupant_count} occupant${room.occupant_count === 1 ? '' : 's'}` : ''}`
                : 'Vacant'}
            </span>
          </div>
        </div>
      </div>

      {/* Body */}
      <div className="px-5 py-4 flex flex-col gap-4 flex-1">

        {/* Tenant info */}
        {room.is_occupied && room.tenant_name ? (
          <div className="flex flex-col gap-1.5">
            <div className="flex items-center gap-2 text-sm">
              <User className="w-4 h-4 text-secondary-400 flex-shrink-0" />
              <span className="font-medium text-secondary-900 dark:text-secondary-100">{room.tenant_name}</span>
            </div>
            {room.tenant_contact && (
              <div className="flex items-center gap-2 text-sm">
                <Phone className="w-4 h-4 text-secondary-400 flex-shrink-0" />
                <span className="text-secondary-600 dark:text-secondary-300">{room.tenant_contact}</span>
              </div>
            )}
            {room.move_in_date && (
              <div className="flex items-center gap-2 text-sm">
                <Calendar className="w-4 h-4 text-secondary-400 flex-shrink-0" />
                <span className="text-secondary-600 dark:text-secondary-300">Moved in: {formatDate(room.move_in_date)}</span>
              </div>
            )}
          </div>
        ) : !room.is_occupied ? (
          <div className="flex items-center gap-2.5">
            <div className="w-9 h-9 rounded-xl bg-secondary-100 dark:bg-secondary-700 flex items-center justify-center flex-shrink-0">
              <DoorOpen className="w-5 h-5 text-secondary-400 dark:text-secondary-500" />
            </div>
            <div>
              <p className="text-sm font-semibold text-secondary-600 dark:text-secondary-400">Available to Let</p>
              {room.vacated_at ? (
                <p className="text-xs text-secondary-400 dark:text-secondary-500">Vacant since {formatDate(room.vacated_at)}</p>
              ) : (
                <p className="text-xs text-secondary-400 dark:text-secondary-500">No previous tenant on record</p>
              )}
            </div>
          </div>
        ) : null}

        {/* Financials */}
        <div className="bg-secondary-50 dark:bg-secondary-700/50 rounded-xl p-3 space-y-2">
          <div className="flex items-center justify-between text-sm">
            <span className="text-secondary-500 dark:text-secondary-400">Monthly Rent</span>
            <span className="font-semibold text-secondary-900 dark:text-secondary-100">{formatCurrency(room.rent_amount)}</span>
          </div>
          {currentBill && currentBill.electricity_amount > 0 && (
            <div className="flex items-center justify-between text-sm">
              <span className="text-secondary-500 dark:text-secondary-400">Current Electricity</span>
              <span className="font-semibold text-secondary-900 dark:text-secondary-100">{formatCurrency(currentBill.electricity_amount)}</span>
            </div>
          )}
          {currentBill && (
            <div className="flex items-center justify-between text-sm border-t border-secondary-200 dark:border-secondary-600 pt-2">
              <span className="font-medium text-secondary-700 dark:text-secondary-300">Total Monthly Due</span>
              <span className="font-bold text-secondary-900 dark:text-secondary-100">{formatCurrency(currentBill.total_amount)}</span>
            </div>
          )}
          {currentBill && (
            <p className="text-xs text-secondary-400 dark:text-secondary-500">Due on day {room.rent_due_date} of each month</p>
          )}
        </div>

        {/* Outstanding — severity-tiered */}
        {totalOutstanding > 0 && (() => {
          const n = overdueMonths.length;
          const severity = n >= 6 ? 'critical' : n >= 3 ? 'high' : 'medium';
          const cls = {
            critical: {
              card: 'bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800',
              label: 'text-red-700 dark:text-red-400', value: 'text-red-800 dark:text-red-300',
              sub: 'text-red-500 dark:text-red-400',
              badge: 'bg-red-100 dark:bg-red-800/40 text-red-700 dark:text-red-300 hover:bg-red-200 dark:hover:bg-red-800/70',
            },
            high: {
              card: 'bg-orange-50 dark:bg-orange-900/20 border-orange-200 dark:border-orange-800',
              label: 'text-orange-700 dark:text-orange-400', value: 'text-orange-800 dark:text-orange-300',
              sub: 'text-orange-500 dark:text-orange-400',
              badge: 'bg-orange-100 dark:bg-orange-800/40 text-orange-700 dark:text-orange-300 hover:bg-orange-200 dark:hover:bg-orange-800/70',
            },
            medium: {
              card: 'bg-amber-50 dark:bg-amber-900/20 border-amber-200 dark:border-amber-800',
              label: 'text-amber-700 dark:text-amber-400', value: 'text-amber-800 dark:text-amber-300',
              sub: 'text-amber-500 dark:text-amber-400',
              badge: 'bg-amber-100 dark:bg-amber-800/40 text-amber-700 dark:text-amber-300 hover:bg-amber-200 dark:hover:bg-amber-800/70',
            },
          }[severity];
          return (
            <div className={`border rounded-xl p-3 ${cls.card}`}>
              <div className="flex items-center justify-between mb-1">
                <span className={`text-sm font-semibold ${cls.label}`}>Total Outstanding</span>
                <span className={`text-sm font-bold ${cls.value}`}>{formatCurrency(totalOutstanding)}</span>
              </div>
              {overdueMonths.length > 0 && (
                <>
                  <p className={`text-xs mb-1.5 ${cls.sub}`}>{n} overdue month{n !== 1 ? 's' : ''}:</p>
                  <div className="flex flex-wrap gap-1">
                    {overdueMonths.slice(0, 6).map((month, index) => (
                      <button
                        key={index}
                        onClick={() => handleBadgeClick(month.month, month.year)}
                        className={`px-2 py-0.5 text-xs rounded-full transition-colors ${cls.badge}`}
                      >
                        {getMonthName(month.month)} {month.year}
                      </button>
                    ))}
                    {overdueMonths.length > 6 && (
                      <span className={`px-2 py-0.5 text-xs rounded-full ${cls.badge}`}>
                        +{overdueMonths.length - 6} more
                      </span>
                    )}
                  </div>
                </>
              )}
            </div>
          );
        })()}

        {/* Credit */}
        {availableCredits > 0 && (
          <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-xl p-3 flex items-center justify-between">
            <span className="text-sm font-semibold text-green-700 dark:text-green-400">Available Credit</span>
            <span className="text-sm font-bold text-green-800 dark:text-green-300">{formatCurrency(availableCredits)}</span>
          </div>
        )}

        {/* View Occupants button — only when there are occupants */}
        {room.occupant_count > 0 && (
          <button
            onClick={onViewOccupants}
            className="w-full mt-1 flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl text-sm font-semibold border-2 border-primary-500 dark:border-primary-400 text-primary-600 dark:text-primary-400 hover:bg-primary-50 dark:hover:bg-primary-900/20 transition-colors"
          >
            <Users className="w-4 h-4" />
            View {room.occupant_count} Occupant{room.occupant_count === 1 ? '' : 's'}
          </button>
        )}

        {/* Mark Vacant button — only for occupied rooms */}
        {room.is_occupied && (
          <button
            onClick={onMarkVacant}
            className="w-full flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl text-sm font-semibold border-2 border-amber-500 dark:border-amber-400 text-amber-600 dark:text-amber-400 hover:bg-amber-50 dark:hover:bg-amber-900/20 transition-colors"
          >
            <DoorOpen className="w-4 h-4" />
            Mark as Vacant
          </button>
        )}
      </div>
    </motion.div>
  );
};

export default RoomsPage;